import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';

import { isNil } from 'lodash';
import { useHistory, useLocation } from 'react-router';

/**
 * Custom hook that provides a useState()-like API for managing
 * URL search parameters.
 * @param param The URL search parameter name.
 * @param defaultValue The value to return if the parameter is not set.
 */
export function useSearchParam(
    param: string,
    defaultValue?: string
): [string | undefined, Dispatch<SetStateAction<string | undefined>>] {
    const location = useLocation();
    const history = useHistory();

    const value =
        useMemo(() => {
            return new URLSearchParams(location.search).get(param);
        }, [location.search]) ?? defaultValue;

    const setParam = useCallback(
        (action: SetStateAction<string | undefined>) => {
            const newValue = handleSetStateAction(action, value);
            if (newValue !== value) {
                const newSearchParams = new URLSearchParams(location.search);
                if (isNil(newValue)) {
                    newSearchParams.delete(param);
                } else {
                    newSearchParams.set(param, newValue);
                }
                history.push(location.pathname + '?' + newSearchParams.toString());
            }
        },
        [value, location.search]
    );

    return [value, setParam];
}

function handleSetStateAction<T extends number | string | boolean | undefined>(
    action: SetStateAction<T>,
    curState: T
): T {
    if (typeof action === 'function') {
        return action(curState);
    } else {
        return action;
    }
}
